'use client';

import { useEffect, useRef, type ReactNode } from 'react';
import { useSectionDwell } from '@/hooks/useSectionDwell';
import { cn } from '@/utils/cn';
import { useSalesCallback } from './SalesCallbackProvider';

interface SalesCallbackSectionProps {
  children: ReactNode;
  className?: string;
  /** Dwell time before the Talk to Sales tab appears (ms). */
  dwellMs?: number;
  /**
   * `section` — reveal after the wrapped block stays in view (default, home sections).
   * `page` — reveal after the user stays on the page from initial load.
   */
  mode?: 'section' | 'page';
}

/**
 * Wrap any page section to reveal the global Talk to Sales tab after the
 * user stays on the section for the configured dwell time.
 */
export default function SalesCallbackSection({
  children,
  className,
  dwellMs = 5000,
  mode = 'section',
}: SalesCallbackSectionProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { showTab, isTabVisible } = useSalesCallback();

  useSectionDwell(ref, {
    dwellMs,
    enabled: !isTabVisible && mode === 'section',
    onDwell: showTab,
  });

  useEffect(() => {
    if (mode !== 'page' || isTabVisible) return;

    const timer = setTimeout(showTab, dwellMs);
    return () => clearTimeout(timer);
  }, [dwellMs, isTabVisible, mode, showTab]);

  return (
    <div ref={ref} className={cn('sales-callback-section', className)}>
      {children}
    </div>
  );
}
